import type { ModuleName } from '@contexts/modules-provider';

import { CobaltMenu, CobaltMenuItem } from '@cae/cobalt-react/components/menu';
import * as React from 'react';

import type { ModuleSwitcherVariantProps } from '../../ModuleSwitcher.types';

import { MODULE_ILLUSTRATIONS } from '../../ModuleSwitcher.utils';
import styles from './ModuleSwitcherCollapsed.module.css';

const ModuleSwitcherCollapsed = React.memo(
  ({
    selectedModule,
    modules,
    hasMultipleModules,
    onModuleSelect,
    onModuleSwitcherToggle,
  }: Readonly<ModuleSwitcherVariantProps>): React.ReactElement => {
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    const Illustration = MODULE_ILLUSTRATIONS[selectedModule.name];

    const handleClose = React.useCallback((): void => {
      setIsMenuOpen(false);
      triggerRef.current?.focus();
    }, []);

    const handleSelect = React.useCallback(
      (moduleName: ModuleName): void => {
        setIsMenuOpen(false);
        if (moduleName === selectedModule.name) return;
        void onModuleSelect(moduleName);
      },
      [selectedModule.name, onModuleSelect]
    );

    const handleTriggerClick = React.useCallback((): void => {
      onModuleSwitcherToggle();
      setIsMenuOpen(prev => !prev);
    }, [onModuleSwitcherToggle]);

    if (!hasMultipleModules) {
      return (
        <div
          className={styles.moduleSwitcherCollapsed}
          data-testid="module-switcher"
          aria-hidden="true"
        >
          <Illustration />
        </div>
      );
    }

    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          className={styles.moduleSwitcherCollapsed}
          aria-label={`Switch module, currently ${selectedModule.name}`}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
          onClick={handleTriggerClick}
          data-testid="module-switcher-trigger"
        >
          <Illustration />
        </button>

        <CobaltMenu
          isOpen={isMenuOpen}
          anchorEl={triggerRef.current}
          position="bottom-start"
          onClose={handleClose}
          className={styles.moduleMenu}
          aria-label="Switch module"
        >
          {modules.map(module => {
            const isSelected = module.name === selectedModule.name;
            return (
              <CobaltMenuItem
                key={module.name}
                onClick={() => handleSelect(module.name)}
                className={
                  isSelected ? styles.moduleMenuItemSelected : undefined
                }
                aria-selected={isSelected}
                data-testid={`module-option-${module.name}`}
              >
                {module.name}
              </CobaltMenuItem>
            );
          })}
        </CobaltMenu>
      </>
    );
  }
);

ModuleSwitcherCollapsed.displayName = 'ModuleSwitcherCollapsed';

export { ModuleSwitcherCollapsed };
